import {
  groupTranscriptParagraphs,
  joinSegmentTexts,
  type TranscriptParagraphSegment,
} from './groupTranscriptParagraphs';
import { formatMeetingType, MEETING_TYPE_OPTIONS } from './meetingLabels';

export type MeetingExportContext = {
  title: string;
  meetingDate: string | null;
  meetingType: string | null;
  clientName: string | null;
  axiumParticipantNames: readonly string[];
};

export type AnalysisPromptId =
  | 'summary'
  | 'presale'
  | 'client_work'
  | 'internal'
  | 'followup'
  | 'action_items'
  | 'meeting_protocol'
  | 'custom';

export type AnalysisPromptOption = {
  id: Exclude<AnalysisPromptId, 'custom'>;
  label: string;
  description: string;
  body: string;
};

const MEETING_DATE_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const ANALYSIS_PROMPTS: readonly AnalysisPromptOption[] = [
  {
    id: 'summary',
    label: 'Итоги встречи',
    description: 'Кратко структурирует основные решения, договорённости и задачи',
    body: [
      'Ты — помощник по анализу деловых встреч. Твоя задача — подготовить структурированные итоги встречи на основе расшифровки разговора.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Опирайся только на факты из расшифровки — не выдумывай информацию',
      '• Пиши кратко и по делу — без воды и повторений',
      '• Соблюдай структуру ответа',
      '',
      'СТРУКТУРА ОТВЕТА:',
      '1. Краткое содержание (2-3 предложения)',
      '2. Основные решения',
      '3. Договорённости',
      '4. Задачи, ответственные и сроки',
      '5. Открытые вопросы',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'presale',
    label: 'Анализ пресейл-встречи',
    description: 'Извлекает потребности клиента, бюджет, возражения и следующие шаги',
    body: [
      'Ты — помощник по анализу пресейл-встреч. Твоя задача — извлечь из расшифровки ключевую информацию о потребностях клиента и стадии переговоров.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Используй только факты из расшифровки — не додумывай информацию',
      '• Если что-то не упоминалось в разговоре — явно укажи это',
      '• Пиши чётко и структурированно',
      '',
      'СТРУКТУРА ОТВЕТА:',
      '1. Потребности клиента',
      '2. Проблемы и ограничения',
      '3. Критерии выбора решения',
      '4. Возражения и сомнения',
      '5. Бюджет и сроки (если назывались)',
      '6. Кто принимает решение',
      '7. Следующие шаги',
      '8. Что осталось выяснить',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'client_work',
    label: 'Рабочая встреча с клиентом',
    description: 'Фиксирует решения, обещания сторон, задачи и риски проекта',
    body: [
      'Ты — помощник по анализу рабочих встреч с клиентами. Твоя задача — зафиксировать договорённости и план действий на основе расшифровки.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Опирайся только на факты из расшифровки — не выдумывай информацию',
      '• Чётко разграничивай обещания каждой из сторон',
      '• Выделяй риски и спорные моменты',
      '',
      'СТРУКТУРА ОТВЕТА:',
      '1. Принятые решения',
      '2. Обещания каждой стороны',
      '3. Задачи, ответственные и сроки',
      '4. Риски',
      '5. Спорные вопросы',
      '6. План дальнейшей работы',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'internal',
    label: 'Внутренняя встреча команды',
    description: 'Структурирует решения, задачи, ответственных и вопросы руководству',
    body: [
      'Ты — помощник по анализу внутренних встреч. Твоя задача — зафиксировать решения, задачи и ответственных на основе расшифровки.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Используй только факты из расшифровки — не додумывай информацию',
      '• Чётко указывай ответственных за задачи',
      '• Выделяй вопросы, которые требуют решения руководителя',
      '',
      'СТРУКТУРА ОТВЕТА:',
      '1. Решения',
      '2. Поставленные задачи',
      '3. Ответственные',
      '4. Сроки',
      '5. Открытые вопросы',
      '6. Вопросы, требующие решения руководителя',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'followup',
    label: 'Follow-up письмо клиенту',
    description: 'Готовит краткое сообщение с договорённостями и следующими шагами',
    body: [
      'Ты — помощник по составлению follow-up писем. Твоя задача — подготовить короткое человеческое сообщение клиенту на основе расшифровки встречи.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Зафиксируй договорённости',
      '• Перечисли следующие шаги',
      '• Используй только факты из расшифровки — не добавляй информацию',
      '• Пиши живым языком — без канцелярита и штампов («довожу до вашего сведения», «на основании вышеизложенного» и т.п.)',
      '• Не используй слова-маркеры текстов от ИИ: «рад», «с нетерпением жду», «погрузиться», «расширить возможности»',
      '• Пиши кратко — 3-5 абзацев',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'action_items',
    label: 'Задачи и ответственные',
    description: 'Извлекает конкретные задачи с исполнителями и сроками',
    body: [
      'Ты — помощник по извлечению задач из встреч. Твоя задача — составить чёткий список задач с ответственными и сроками на основе расшифровки.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Используй только факты из расшифровки — не выдумывай задачи',
      '• Формулируй задачи конкретно — что именно нужно сделать',
      '• Если срок или ответственный не назывались — укажи это явно',
      '• Указывай контекст задачи, если он важен для понимания',
      '',
      'ФОРМАТ ОТВЕТА:',
      'Для каждой задачи укажи:',
      '• Что нужно сделать',
      '• Кто ответственный',
      '• Срок',
      '• Контекст (если нужен)',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
  {
    id: 'meeting_protocol',
    label: 'Протокол встречи для почты',
    description: 'Составляет формальный протокол для отправки участникам',
    body: [
      'Ты — помощник по составлению протоколов встреч. Твоя задача — подготовить формальный протокол на основе расшифровки для отправки участникам.',
      '',
      'ТРЕБОВАНИЯ:',
      '• Опирайся только на факты из расшифровки',
      '• Используй деловой стиль — чётко и структурированно',
      '• Не добавляй вводные фразы и не делай выводы, которых не было',
      '',
      'СТРУКТУРА ПРОТОКОЛА:',
      '1. Участники',
      '2. Повестка (если называлась)',
      '3. Обсуждённые вопросы',
      '4. Принятые решения',
      '5. Задачи и ответственные',
      '6. Следующая встреча (если назначалась)',
      '',
      'Ниже приведена расшифровка встречи.',
    ].join('\n'),
  },
];

export function formatExportTimecode(startMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(startMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function formatExportMeetingDate(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.trim() === '') {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value.trim());
  if (!match) {
    return value.trim();
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) {
    return value.trim();
  }
  return MEETING_DATE_FORMATTER.format(date);
}

function meetingTypeLabel(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const found = MEETING_TYPE_OPTIONS.find((item) => item.value === value);
  if (found) {
    return found.label;
  }
  const formatted = formatMeetingType(value);
  return formatted === 'Не указан' ? null : formatted;
}

export function buildMeetingHeaderLines(meeting: MeetingExportContext): string[] {
  const lines: string[] = [];
  const title = meeting.title.trim();
  if (title !== '') {
    lines.push(`Встреча: ${title}`);
  }

  const dateLabel = formatExportMeetingDate(meeting.meetingDate);
  if (dateLabel !== null) {
    lines.push(`Дата: ${dateLabel}`);
  }

  const typeLabel = meetingTypeLabel(meeting.meetingType);
  if (typeLabel !== null) {
    lines.push(`Тип: ${typeLabel}`);
  }

  if (meeting.meetingType === 'INTERNAL') {
    lines.push('Клиент: Внутренняя');
  } else if (meeting.clientName !== null && meeting.clientName.trim() !== '') {
    lines.push(`Клиент: ${meeting.clientName.trim()}`);
  }

  const axium = meeting.axiumParticipantNames.map((name) => name.trim()).filter(Boolean);
  if (axium.length > 0) {
    lines.push(`Участники AXIUM: ${axium.join(', ')}`);
  }

  return lines;
}

function speakerLabelForParagraph(
  paragraphSegmentIds: readonly string[],
  segmentsById: Map<string, TranscriptParagraphSegment>,
): string | null {
  for (const id of paragraphSegmentIds) {
    const segment = segmentsById.get(id);
    if (segment?.speaker != null && segment.speaker.displayName.trim() !== '') {
      return segment.speaker.displayName.trim();
    }
  }
  return null;
}

/**
 * Полный текст расшифровки для буфера: реплики по порядку с таймкодами и спикерами.
 * Группировка совпадает с абзацами UI (соседние сегменты одного спикера).
 */
export function buildTranscriptBody(segments: readonly TranscriptParagraphSegment[]): string {
  if (segments.length === 0) {
    return '';
  }

  const paragraphs = groupTranscriptParagraphs(segments);
  const byId = new Map(segments.map((segment) => [segment.id, segment]));
  const blocks: string[] = [];

  for (const paragraph of paragraphs) {
    const speaker = speakerLabelForParagraph(paragraph.segmentIds, byId);
    const header =
      speaker === null
        ? `[${formatExportTimecode(paragraph.startMs)}]`
        : `[${formatExportTimecode(paragraph.startMs)}] ${speaker}`;
    blocks.push(`${header}\n${paragraph.text}`);
  }

  return blocks.join('\n\n');
}

export function buildPlainTranscriptExport(
  meeting: MeetingExportContext,
  segments: readonly TranscriptParagraphSegment[],
): string {
  const header = buildMeetingHeaderLines(meeting);
  const body = buildTranscriptBody(segments);
  const parts: string[] = [];

  if (header.length > 0) {
    parts.push(header.join('\n'));
  }
  parts.push('РАСШИФРОВКА');
  if (body !== '') {
    parts.push(body);
  }

  return parts.join('\n\n').trim() + '\n';
}

export function buildPromptTranscriptExport(
  promptBody: string,
  meeting: MeetingExportContext,
  segments: readonly TranscriptParagraphSegment[],
): string {
  const header = buildMeetingHeaderLines(meeting);
  const body = buildTranscriptBody(segments);
  const parts = [
    'ЗАДАЧА ДЛЯ АНАЛИЗА',
    '',
    promptBody.trim(),
    '',
    'СВЕДЕНИЯ О ВСТРЕЧЕ',
    '',
    header.length > 0 ? header.join('\n') : 'Сведения о встрече не указаны.',
    '',
    'РАСШИФРОВКА',
    '',
    body !== '' ? body : 'Текст расшифровки отсутствует.',
  ];

  return `${parts.join('\n').trim()}\n`;
}

/** Для тестов: склеивание текста сегментов без потери порядка. */
export function joinAllSegmentText(segments: readonly TranscriptParagraphSegment[]): string {
  return joinSegmentTexts(segments.map((segment) => segment.text));
}
